// Recovery for a route chunk that does not load.
//
// Every route is a React.lazy chunk with a content hash in its name. A page
// cached from before a deploy (an in-app browser is the usual case) names a hash
// the host no longer serves, the import rejects, and the prerendered page never
// hydrates. One reload gets fresh HTML that names the current hashes.
//
// ONLY A ROUTE CHUNK MAY RELOAD THE PAGE. The decision is taken where the route
// import fails (`routeChunk`, which App.client.tsx wraps around every page
// import, and pages/blog/post.tsx around a post's own text, which is part of
// that route, E-429), not on Vite's global `vite:preloadError` event. A post's
// chunk suspends inside the same root <Suspense> as the page, so the guard is
// cleared only once both have loaded. That event fires for
// every failed dynamic import, and the other lazy chunks — posthog-js, the
// story and demo engines — load after the route has rendered and handle their
// own failures. Reloading for one of those would reload a page that was
// already working; and a chunk that fails every time (an ad blocker on the
// analytics chunk, say) would reload it without end, because the guard below
// is cleared each time a route renders.
//
// The sessionStorage key is the loop guard: it is set before the reload and
// cleared only when a route has rendered, so a route chunk that fails again
// after the reload rejects as it always did and goes to error tracking. With
// no sessionStorage there is no guard, so there is no reload either.

const RELOAD_KEY = "capnolog_chunk_reload";
/** The guard's value while a reload is owed a recovery report. */
const RELOAD_PENDING = "1";
/**
 * The guard's value once the reload did NOT fix it. The key stays set so the
 * tab cannot reload again, but the next route that renders — a reload by hand,
 * a later navigation — is not a recovery this code made, and must not count
 * as one.
 */
const RELOAD_FAILED = "failed";

let reloading = false;

/** True between the reload call and the unload, when errors are expected noise. */
export function isReloadingForChunk(): boolean {
  return reloading;
}

/**
 * Wrap a route chunk's import. A failure reloads the page once; the import then
 * stays pending until the unload, so React keeps the prerendered page on screen
 * instead of throwing. A second failure in a row rejects as the import would
 * have, and reaches error tracking.
 */
export function routeChunk<T>(load: () => Promise<T>): Promise<T> {
  return load().catch((err: unknown) => {
    if (!reloadOnce()) throw err;
    return new Promise<T>(() => {});
  });
}

/** Set the guard and reload. False when the guard was already set, or cannot be. */
function reloadOnce(): boolean {
  if (reloading) return true;
  try {
    if (sessionStorage.getItem(RELOAD_KEY) !== null) {
      sessionStorage.setItem(RELOAD_KEY, RELOAD_FAILED);
      return false;
    }
    sessionStorage.setItem(RELOAD_KEY, RELOAD_PENDING);
  } catch {
    return false;
  }
  reloading = true;
  window.location.reload();
  return true;
}

/**
 * Called when a route has rendered. Clears the guard, so a later deploy can
 * recover the same tab again, and returns true once — only on the page load
 * that a reload above recovered. A guard left by a reload that did not help
 * is cleared too, but reports nothing: that render was not this code's doing.
 */
export function consumeChunkReload(): boolean {
  try {
    const state = sessionStorage.getItem(RELOAD_KEY);
    if (state === null) return false;
    sessionStorage.removeItem(RELOAD_KEY);
    return state === RELOAD_PENDING;
  } catch {
    return false;
  }
}
